import { createContext, useContext, type FC, type ReactNode } from 'react';
import { type MessagingSurface } from './MessagingProvider.types';

const MessagingSurfaceContext = createContext<MessagingSurface>('widget');
const ModalContainerContext = createContext<HTMLElement | null>(null);

// Shared context set by the root-level SendBirdProvider in RoutingRoot.
// When true, Sendbird is connected and messaging components can render.
export const SendbirdConnectedContext = createContext(false);

export function useMessagingSurface(): MessagingSurface {
    return useContext(MessagingSurfaceContext);
}

export function useModalContainer(): HTMLElement | null {
    return useContext(ModalContainerContext);
}

export function useSendbirdConnected(): boolean {
    return useContext(SendbirdConnectedContext);
}

/**
 * Thin context wrapper for messaging surfaces. Does NOT create a Sendbird
 * connection — the shared SendBirdProvider lives in RoutingRoot. This
 * component sets the surface type and modal container contexts, and gates
 * children on the Sendbird connection being ready.
 */
export const MessagingProvider: FC<{
    children: ReactNode;
    surface?: MessagingSurface;
    modalContainer?: HTMLElement | null;
}> = ({ children, surface = 'widget', modalContainer = null }) => {
    const connected = useSendbirdConnected();

    return (
        <MessagingSurfaceContext.Provider value={surface}>
            <ModalContainerContext.Provider value={modalContainer}>
                {connected && children}
            </ModalContainerContext.Provider>
        </MessagingSurfaceContext.Provider>
    );
};
